import { useState, useCallback, MouseEvent, useMemo, useRef, FC, CSSProperties } from 'react'
import {
  ColumnVisibility,
  matchingCapabilities,
  MatchingPayoutTicket,
  MatchingPayoutPaymentTicket,
} from '../../core/_models'
import { convertUTCtoMSK } from '../../../../../utils'
import { Table } from '../../../../../components/Table'
import { TableHead } from '../../../../../components/Table/TableHead'
import { TableBody } from '../../../../../components/Table/TableBody'
import { KTIcon } from '../../../../../_metronic/helpers'
import clsx from 'clsx'
import { useIntl } from 'react-intl'
import FinishedPayoutPaymentTicketRow from './FinishedPayoutPaymentTicketRow'
import { AmountCell } from '../common/AmountCell'
import { PayoutRequisites } from '../common/PayoutRequisites'
import { PayoutTokenLink } from '../../../../common/PayoutTokenLink'
import { ClientCell } from '../../../../common/ClientCell'
import { TruncatedValue } from '../../../../common/TruncatedValue'
import { ResendCallbackButton } from '../common/ResendCallbackButton'
import { IsGranted, useAuth } from '../../../auth'
import { EMatchingPayoutTicketStatus } from '../../../../../types/enums'
import { OverpaymentModal } from '../../routes/Overpayments/components/OverpaymentModal/OverpaymentModal'
import { OverpaymentButton } from '../../routes/Overpayments/components/OverpaymentButton/OverpaymentButton'
import { PayoutDisputeModal } from '../../routes/Disputes/components/PayoutDisputeModal/PayoutDisputeModal'
import { RowKebabMenu, RowKebabSlot } from '../../../../common/RowKebabMenu'
import { Dropdown } from 'react-bootstrap'

interface Props {
  ticket: MatchingPayoutTicket
  visibleColumns: number // кол-во видимых колонок родительской таблицы (для colspan)
  visibility: ColumnVisibility
  canResend: boolean
  /** Роль может открыть спор о выплате; открыть ли его по этой строке — решает бэкенд (canOpenDispute). */
  canDispute?: boolean
  onOverpaymentCreated?: (compensationPayoutToken: string) => void
  onDisputeCreated?: () => void
}

const FinishedPayoutTicketItem = ({
  ticket,
  visibleColumns,
  visibility,
  canResend,
  canDispute = false,
  onOverpaymentCreated,
  onDisputeCreated,
}: Props) => {
  const intl = useIntl()
  const { currentUser } = useAuth()
  const [isExpanded, setIsExpanded] = useState<boolean>(false)
  const [overpaymentFor, setOverpaymentFor] = useState<MatchingPayoutPaymentTicket | null>(null)
  const [showDisputeModal, setShowDisputeModal] = useState(false)
  const [showActionsMenu, setShowActionsMenu] = useState(false)
  const hasActions = canResend || canDispute
  const canOpenDispute = canDispute && ticket.canOpenDispute === true
  // На завершённых выплатах родитель всегда в терминальном статусе. Переоплата
  // доступна, когда родительская выплата COMPLETED; для DECLINED колонку и
  // модалку не рендерим вовсе. Остальная логика — как на активных выплатах.
  // CPAYMENT-1726: переоплата открыта и оператору платежей мерчанта — список ролей держим в
  // matchingCapabilities, он обязан совпадать с бэкендом.
  const canUseOverpayment = IsGranted(matchingCapabilities.canCreateOverpayment, currentUser)
  const isParentCompleted = ticket.status === EMatchingPayoutTicketStatus.COMPLETED
  const canOverpay = canUseOverpayment && isParentCompleted

  // Депозиты, по которым переоплата уже заведена в этом сеансе, — кнопка по ним гаснет сразу.
  // Серверный запрет держится, только пока компенсационная выплата не финальна, а она может стать
  // финальной в ту же секунду: перечитанный список тогда снова разрешит переоплату, и второй клик
  // заведёт ещё одну. Ref, а не состояние: перерисовку даёт закрытие модалки в том же обработчике.
  const createdOverpaymentFor = useRef<Set<number>>(new Set())

  // Ссылка «Уже переоплачивается» доступна, если у депозита есть незавершённая компенсация.
  const hasInProgressOverpayment = useCallback(
    (pt: MatchingPayoutPaymentTicket) =>
      canOverpay && !!pt.overpaymentInProgress && !!pt.overpaymentCompensationToken,
    [canOverpay]
  )

  // Кнопка создания доступна для завершённого депозита без активной переоплаты.
  const canCreateOverpayment = useCallback(
    (pt: MatchingPayoutPaymentTicket) =>
      canOverpay &&
      !pt.overpaymentInProgress &&
      pt.status === 'COMPLETED' &&
      !pt.matchReleased &&
      !!pt.matchingTransactionId,
    [canOverpay]
  )

  const renderOverpaymentCell = useCallback(
    (pt: MatchingPayoutPaymentTicket) => {
      if (hasInProgressOverpayment(pt)) {
        return (
          <div className='d-flex flex-column align-items-end gap-1'>
            <span className='text-info fw-semibold'>В процессе переоплаты</span>
            <CopyableToken value={pt.overpaymentCompensationToken} />
          </div>
        )
      }

      if (!canCreateOverpayment(pt)) return null

      const alreadyCreated =
        pt.matchingTransactionId != null &&
        createdOverpaymentFor.current.has(pt.matchingTransactionId)

      return (
        <OverpaymentButton
          history={pt.overpaymentHistory}
          disabled={alreadyCreated}
          onClick={() => setOverpaymentFor(pt)}
        />
      )
    },
    [hasInProgressOverpayment, canCreateOverpayment]
  )

  // Заголовок колонки действий показываем только если хотя бы у одного депозита
  // есть кнопка или ссылка (иначе пустой столбец без шапки не нужен).
  const hasAnyOverpaymentCell = useMemo(
    () =>
      (ticket.paymentTicketList ?? []).some(
        (pt) => hasInProgressOverpayment(pt) || canCreateOverpayment(pt)
      ),
    [ticket.paymentTicketList, hasInProgressOverpayment, canCreateOverpayment]
  )

  const isExpandable = useMemo(() => {
    return visibility.paymentTicketList && ticket.paymentTicketList?.length > 0
  }, [ticket, visibility])

  const toggleOpen = useCallback(
    (e: MouseEvent<HTMLTableRowElement>) => {
      if (!isExpandable) return
      // Исключаем клики по ссылкам, кнопкам, меню действий и окну спора: окно — портал, но React
      // доносит его события до строки, и любой клик в нём иначе сворачивал бы строку.
      if ((e.target as HTMLElement)?.closest('a, button, .dropdown, .dropdown-menu, .modal')) return
      setIsExpanded((prev) => !prev)
    },
    [isExpandable]
  )

  const isAmountChanged = useMemo(() => {
    const actualAmount = ticket.expectedAmount !== null && ticket.matchingRemainder !== undefined 
      ? ticket.expectedAmount - ticket.matchingRemainder 
      : ticket.amount;
    
    return (
      actualAmount !== null &&
      ticket.expectedAmount !== null &&
      actualAmount !== ticket.expectedAmount
    )
  }, [ticket])

  return (
    <>
      <tr className={clsx(isExpandable && 'cursor-pointer')} onClick={toggleOpen}>
        <td className='text-center align-middle'>
          <KTIcon
            iconName={isExpanded ? 'minus-square' : 'plus-square'}
            className={`fs-3 text-muted ${isExpandable ? 'cursor-pointer' : 'opacity-50'}`}
          />
        </td>
        {visibility.ticketToken && (
          <td>
            <div className='fw-bold'>
              <PayoutTokenLink token={ticket.ticketToken} />
              {ticket.payoutDisputeStatus === 'OPEN' && (
                <span className='badge badge-light-warning ms-2'>Открыт диспут</span>
              )}
            </div>
            {ticket.recreatedToken && (
              <div className='mt-2 fs-7'>
                <span 
                  onClick={(e) => e.stopPropagation()}
                >
                  Пересозданный тикет: 
                </span>
                <PayoutTokenLink token={ticket.recreatedToken} />
              </div>
            )}
          </td>
        )}
        {visibility.createdDate && <td>{convertUTCtoMSK(ticket.createdAt)}</td>}
        {visibility.paymentMethod && <td>{ticket.paymentMethod || '-'}</td>}
        {visibility.completionDate && (
          <td>{ticket.completionDate ? convertUTCtoMSK(ticket.completionDate) : '-'}</td>
        )}
        {visibility.amount && (
          <AmountCell
            amount={ticket?.expectedAmount !== null && ticket?.matchingRemainder !== undefined 
              ? ticket.expectedAmount - ticket.matchingRemainder 
              : ticket?.amount}
            expectedAmount={ticket?.expectedAmount}
            isAmountChanged={isAmountChanged}
          />
        )}
        {visibility.user && <ClientCell externalId={ticket.externalUserId} token={ticket.clientToken} />}
        {visibility.merchantId && (
          <td>
            <TruncatedValue value={ticket.merchantTransactionId} empty='-' />
          </td>
        )}
        {visibility.merchant && <td>{ticket.merchant}</td>}
        {visibility.payoutRequisite && (
          <td>
            {/* CPAYMENT-1713: те же каналы, что в активном списке — вид блока одинаков в обоих. */}
            <div className="text-dark">
              <PayoutRequisites
                requisites={ticket.requisites}
                fallback={{
                  identifier: ticket.matchingTicketRequisite,
                  bank: ticket.matchingTicketBank,
                  cardholder: ticket.matchingTicketCardholder,
                }}
              />
            </div>
          </td>
        )}
        {visibility.status && (
          <td>{intl.formatMessage({ id: 'ticket.status.' + ticket.status })}</td>
        )}
        {hasActions && (
          <td className='text-end align-middle'>
            <div className='d-flex align-items-center justify-content-end gap-2'>
              {canResend && <ResendCallbackButton token={ticket.ticketToken} />}
              <RowKebabSlot>
                {canOpenDispute && (
                  <RowKebabMenu title='Действия по выплате' show={showActionsMenu} onToggle={setShowActionsMenu}>
                    <Dropdown.Item
                      as='button'
                      type='button'
                      className='d-flex align-items-center'
                      onClick={() => {
                        setShowActionsMenu(false)
                        setShowDisputeModal(true)
                      }}
                    >
                      <KTIcon iconName='shield-cross' className='fs-3 me-2' />
                      Создать диспут
                    </Dropdown.Item>
                  </RowKebabMenu>
                )}
              </RowKebabSlot>
            </div>
            {showDisputeModal && (
              <PayoutDisputeModal
                show={showDisputeModal}
                onClose={() => setShowDisputeModal(false)}
                payoutToken={ticket.ticketToken}
                onCreated={() => onDisputeCreated?.()}
              />
            )}
          </td>
        )}
      </tr>
      {overpaymentFor && (
        <OverpaymentModal
          show={overpaymentFor !== null}
          onClose={() => setOverpaymentFor(null)}
          onCreated={(record) => {
            if (overpaymentFor.matchingTransactionId != null) {
              createdOverpaymentFor.current.add(overpaymentFor.matchingTransactionId)
            }
            setOverpaymentFor(null)
            onOverpaymentCreated?.(record.compensationPayoutTicket.token)
          }}
          context={{
            matchingTransactionId: overpaymentFor.matchingTransactionId as number,
            amount: overpaymentFor.amount ?? 0,
            currency: 'RUB',
            paymentMethod: ticket.paymentMethod,
            requisite: ticket.matchingTicketRequisite ?? '',
            recipient: {
              externalId: ticket.externalUserId,
              merchantToken: ticket.merchant,
            },
            culprit: {
              externalId: overpaymentFor.externalUserId ?? '',
              merchantToken: overpaymentFor.merchant,
            },
            history: overpaymentFor.overpaymentHistory,
          }}
        />
      )}

      {isExpanded && ticket.paymentTicketList?.length ? (
        <tr>
          <td colSpan={visibleColumns + (hasActions ? 1 : 0)} className='p-0'>
            <div className='m-4 border bg-background-light'>
              <Table>
                <TableHead>
                  <tr>
                    <th className='min-w-120px ps-8'>
                      <small>ID тикета</small>
                    </th>
                    {visibility.createdDate && (
                      <th className='min-w-100px'>
                        <small>Дата создания</small>
                      </th>
                    )}
                    {visibility.completionDate && (
                      <th className='min-w-100px'>
                        <small>Дата закрытия</small>
                      </th>
                    )}
                    {visibility.amount && (
                      <th className='min-w-160px'>
                        <small>Сумма (запрос / факт)</small>
                      </th>
                    )}
                    {visibility.user && (
                      <th className='min-w-100px'>
                        <small>Клиент</small>
                      </th>
                    )}
                    {visibility.merchantId && (
                      <th className='min-w-120px'>
                        <small>ID транзакции</small>
                      </th>
                    )}
                    {visibility.merchant && (
                      <th className='min-w-120px'>
                        <small>Мерчант</small>
                      </th>
                    )}
                    {visibility.status && (
                      <th className='min-w-140px'>
                        <small>Статус</small>
                      </th>
                    )}
                    {visibility.file && (
                      <th className='min-w-120px text-center align-middle'>
                        <small>Чек</small>
                      </th>
                    )}
                    {visibility.authorId && (
                      <th className='min-w-100px text-center align-middle'>
                        <small>Author ID</small>
                      </th>
                    )}
                    {hasAnyOverpaymentCell && (
                      <th className='min-w-120px text-end align-middle'>
                        <small>Переоплата</small>
                      </th>
                    )}
                  </tr>
                </TableHead>
                <TableBody>
                  {ticket.paymentTicketList.map((pt, idx) => (
                    <FinishedPayoutPaymentTicketRow
                      key={pt.merchantTransactionId || idx}
                      ticket={pt}
                      visibility={visibility}
                      actionSlot={renderOverpaymentCell(pt)}
                      showActionColumn={hasAnyOverpaymentCell}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
          </td>
        </tr>
      ) : null}
    </>
  )
}

// Мелкий моноширинный токен, кликабельный для копирования. При наведении
// появляется обводка с иконкой — как CopyableCell в аудит-логах интеграции.
const CopyableToken: FC<{ value: string | null | undefined }> = ({ value }) => {
  const [hovered, setHovered] = useState(false)
  const [copied, setCopied] = useState(false)

  if (!value) return null

  const handleCopy = (e: MouseEvent) => {
    e.stopPropagation()
    navigator.clipboard
      ?.writeText(value)
      .then(() => {
        setCopied(true)
        setTimeout(() => setCopied(false), 1200)
      })
      .catch(() => undefined)
  }

  const textStyle: CSSProperties = {
    fontFamily: 'monospace',
    fontSize: '0.75rem',
    whiteSpace: 'nowrap',
  }

  return (
    <span
      style={{ position: 'relative', display: 'inline-block', whiteSpace: 'nowrap', cursor: 'pointer' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={handleCopy}
    >
      <span className='text-muted' style={textStyle}>
        {value}
      </span>
      {hovered && (
        <span
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          onClick={handleCopy}
          style={{
            position: 'absolute',
            top: '-4px',
            right: '-8px',
            bottom: '-4px',
            width: 'max-content',
            border: '1px solid var(--bs-border-color)',
            borderRadius: '5px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.13)',
            background: 'var(--bs-body-bg)',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            paddingLeft: '8px',
            paddingRight: '8px',
            zIndex: 10,
          }}
        >
          <span style={textStyle}>{value}</span>
          <KTIcon
            iconName={copied ? 'check-circle' : 'copy'}
            className={`fs-5 ${copied ? 'text-success' : 'text-gray-500'}`}
          />
        </span>
      )}
    </span>
  )
}

export default FinishedPayoutTicketItem
